<template>
    <div v-if="show" class="mp-overlay" @click.self="onClose">
        <div class="mp-modal">
            <!-- Header -->
            <div class="mp-header">
                <span class="mp-title">
                    <i class="fa fa-calculator"></i>
                    Editor de Equação
                    <small v-if="indicatorName" class="mp-indicator-tag">— {{ indicatorName }}</small>
                </span>
                <button class="mp-close-btn" @click="onClose">
                    <i class="fa fa-times"></i>
                </button>
            </div>

            <!-- Body -->
            <div class="mp-body">
                <!-- Left: harvest plan variables -->
                <section class="mp-left">
                    <!-- Column title -->
                    <div class="harvest-col-header">
                        <i class="fa fa-th-list"></i>
                        Variáveis de Plano de Safra
                    </div>

                    <!-- Search -->
                    <div class="harvest-search-wrap">
                        <i class="fa fa-search harvest-search-icon"></i>
                        <input class="harvest-search" v-model="harvestSearch" type="text"
                            placeholder="Filtrar correntes, operações, atributos">
                    </div>

                    <!-- List -->
                    <div class="harvest-list">
                        <div v-if="loadingHarvest" class="harvest-loading">
                            <i class="fa fa-spinner fa-spin"></i> Carregando...
                        </div>
                        <div v-else-if="filteredHarvest.length === 0" class="harvest-loading">
                            Nenhum item encontrado
                        </div>
                        <div v-for="(item, idx) in filteredHarvest" :key="`harvest-${idx}`"
                            class="harvest-item"
                            :class="{ 'harvest-item--active': selectedHarvestItem && selectedHarvestItem.planTag === item.planTag }"
                            @click="selectHarvestItem(item)"
                            @dblclick="insertHarvestToEquation(item)">
                            <i class="fa fa-bars harvest-item-icon"></i>
                            <span>{{ item.planName || item.planTag }}</span>
                        </div>
                    </div>

                    <!-- Hint -->
                    <div class="mp-dblclick-hint">
                        <i class="fa fa-info-circle"></i>
                        Clique duas vezes em um item para adicioná-lo à equação
                    </div>

                    <!-- Selected item info -->
                    <div class="harvest-selected-info">
                        <div class="harvest-info-field">
                            <span class="harvest-info-label">Mapeamento:</span>
                            <div class="harvest-info-text">{{ selectedHarvestItem ? (selectedHarvestItem.planTag || '') : '' }}</div>
                        </div>
                        <div class="harvest-info-field">
                            <span class="harvest-info-label">Valor:</span>
                            <input type="text" class="harvest-value-input"
                                :value="selectedHarvestItem ? selectedHarvestItem.value : ''"
                                readonly>
                        </div>
                    </div>
                </section>

                <!-- Right: calculator -->
                <section class="mp-right">
                    <!-- Operations grid -->
                    <section class="operations">
                        <!-- Números (grupo local) -->
                        <section class="op-container nums">
                            <p>Números</p>
                            <section>
                                <ReferenceCard v-for="(op, idx) in numbersOps"
                                    :key="`num-${idx}`"
                                    :reference="op" cardType="operation" deleteClass=""
                                    :title="op.tooltip"
                                    @click.native="addToEquation(op, 'number')"
                                    customClass="ops" />
                            </section>
                        </section>
                        <!-- Grupos do mediator -->
                        <section v-for="(item, itemIdx) in operationsAllowed"
                            :key="`${item.group}-${itemIdx}`"
                            :class="`op-container ${item.group}`">
                            <p>{{ item.groupName }}</p>
                            <section>
                                <ReferenceCard v-for="(op, idx) in item.ops"
                                    :key="`${op.label}-${idx}`"
                                    :reference="op" cardType="operation" deleteClass=""
                                    :title="op.tooltip"
                                    @click.native="addToEquation(op, 'operation')"
                                    :customClass="item.group" />
                            </section>
                        </section>
                    </section>

                    <!-- Campo de Edição -->
                    <div class="equation-label">
                        <i class="fa fa-pencil-square-o"></i>
                        Campo de Edição
                    </div>
                    <section class="equation equation-display">
                        <ReferenceCard v-for="(ref, idx) in equationList"
                            :key="`mpref-${idx}`"
                            :reference="ref"
                            :id="`mp-${ref.label}-${idx}-eq`"
                            :cardType="ref.type"
                            deleteClass="deleteClass"
                            customClass=""
                            @click.native="deleteInEquation(ref, idx)" />
                    </section>

                    <div class="eq-actions">
                        <button class="eq-btn eq-btn--clear" @click="clearEquation">
                            <i class="fa fa-trash"></i> Limpar
                        </button>
                        <button class="eq-btn eq-btn--undo" @click="undoEquation">
                            <i class="fa fa-undo"></i> Desfazer
                        </button>
                    </div>

                    <div class="mp-observation">
                        <p class="mp-obs-label">
                            <i class="fa fa-comment-o"></i>
                            Observação da equação
                        </p>
                        <textarea v-model="localComment" class="mp-obs-textarea" rows="3"
                            placeholder="Adicionar observação sobre a equação..."></textarea>
                    </div>
                </section>
            </div>

            <!-- Footer -->
            <div class="mp-footer">
                <button class="mp-btn-cancel" @click="onClose">Cancelar</button>
                <button class="mp-btn-confirm" @click="onSave">
                    <i class="fa fa-check"></i> Confirmar
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import ReferenceCard from '../../indicatorEditor/commons/ReferenceCard.vue';
import MediatorIndicatorEditor from '../../indicatorEditor/MediatorIndicatorEditor';

export default {
    name: 'MetaPlanoModal',
    components: { ReferenceCard },
    props: {
        show: { type: Boolean, default: false },
        indicatorName: { type: String, default: '' },
        equation: { type: String, default: '' },
        comment: { type: String, default: '' },
        reportDate: { type: String, default: '' }
    },
    data() {
        return {
            operationsAllowed: MediatorIndicatorEditor.operationsAllowed,
            numbersOps: [
                { label: '1', isFunc: false, tooltip: '1' },
                { label: '2', isFunc: false, tooltip: '2' },
                { label: '3', isFunc: false, tooltip: '3' },
                { label: '4', isFunc: false, tooltip: '4' },
                { label: '5', isFunc: false, tooltip: '5' },
                { label: '6', isFunc: false, tooltip: '6' },
                { label: '7', isFunc: false, tooltip: '7' },
                { label: '8', isFunc: false, tooltip: '8' },
                { label: '9', isFunc: false, tooltip: '9' },
                { label: '0', isFunc: false, tooltip: '0' },
                { label: '.', isFunc: false, tooltip: 'Ponto Decimal' },
            ],
            equationList: [],
            localComment: '',
            harvestSearch: '',
            selectedHarvestItem: null,
            loadingHarvest: false,
            harvestList: [],
        };
    },
    computed: {
        filteredHarvest() {
            const q = this.harvestSearch.trim().toLowerCase();
            return q
                ? this.harvestList.filter(h =>
                    (h.planTag || '').toLowerCase().includes(q) ||
                    (h.planName || '').toLowerCase().includes(q))
                : this.harvestList;
        },
    },
    mounted() {
        this.initModal();
    },
    watch: {
        show(val) {
            if (val) this.$nextTick(this.initModal);
        },
    },
    methods: {
        initModal() {
            this.localComment = this.comment || '';
            this.selectedHarvestItem = null;
            this.harvestSearch = '';
            this.equationList = this.parseEquationString(this.equation);
            this.loadHarvestData();
        },

        async loadHarvestData() {
            this.loadingHarvest = true;
            try {
                await MediatorIndicatorEditor.getProductionPlanData(this.reportDate || null);
                this.harvestList = [...(MediatorIndicatorEditor.harvestPlanList || [])];
            } catch (e) { /* silent */ }
            this.loadingHarvest = false;
        },

        parseEquationString(str) {
            if (!str || !str.trim()) return [];
            return str.trim().split(/\s+/).map(token => ({
                label: token,
                type: 'operation',
                // Marca literais numéricos para que a edição posterior continue concatenando dígitos.
                isNumber: /^\d+(\.\d+)?$|^\.\d+$/.test(token),
                structure: { label: token, isFunc: false, colorClass: '', tooltip: token }
            }));
        },

        equationToString() {
            return this.equationList.map(r => r.label).join(' ');
        },

        selectHarvestItem(item) {
            this.selectedHarvestItem = item;
        },

        insertHarvestToEquation(item) {
            const label = `ind_plan_${item.planTag}`;
            this.addToEquation({
                label,
                type: 'reference',
                structure: { ...item, desc: `Plano de Safra\nTag: ${item.planTag}\nValor: ${item.value}` }
            }, 'reference');
        },

        addToEquation(op, type) {
            // Números e ponto decimal são concatenados ao chip numérico anterior,
            // de modo que clicar em 3 e depois 0 produza o literal "30" e não "3" "0".
            if (type === 'number') {
                const last = this.equationList[this.equationList.length - 1];
                if (last && last.isNumber) {
                    // Evita um segundo ponto decimal no mesmo número.
                    if (op.label === '.' && last.label.includes('.')) return;
                    const merged = last.label + op.label;
                    this.$set(this.equationList, this.equationList.length - 1, {
                        label: merged,
                        type: 'operation',
                        isNumber: true,
                        structure: { label: merged, isFunc: false, colorClass: '', tooltip: merged }
                    });
                    return;
                }
                this.equationList.push({ label: op.label, type: 'operation', isNumber: true, structure: op });
                return;
            }
            this.equationList.push({ label: op.label, type: type, structure: op });
        },

        deleteInEquation(ref, idx) {
            if (this.equationList[idx] && this.equationList[idx].label === ref.label) {
                this.equationList.splice(idx, 1);
            }
        },

        clearEquation() {
            this.equationList = [];
        },

        undoEquation() {
            this.equationList.pop();
        },

        evaluateEquation(equation) {
            return MediatorIndicatorEditor.evaluatePlanEquation(equation, this.harvestList);
        },

        // Indica se o token produz um valor (número, referência de plano ou constante).
        isValueToken(t) {
            if (t === undefined) return false;
            return /^\d+(\.\d+)?$|^\.\d+$/.test(t)
                || /^ind_plan_/.test(t)
                || t === '0' || t === '-9999' || t === 'true' || t === 'false';
        },

        // Checagens estruturais que a validação compartilhada (validateEquation) não cobre:
        // operador binário no início, parênteses fora de ordem, operando colado a parêntese
        // e ponto decimal isolado.
        validateStructure(labels) {
            if (!labels.length) return { status: true, msg: '' };

            // Operadores que não podem iniciar uma expressão (+/- são aceitos como sinal).
            const cantLead = ['*', '/', '^', '=', '<', '>', '<=', '>=', '<>', 'AND', 'OR', ',', ':', "'", 'then', 'else'];
            if (cantLead.includes(labels[0])) {
                return { status: false, msg: `A equação não pode começar com "${labels[0]}".` };
            }

            let depth = 0;
            for (let i = 0; i < labels.length; i++) {
                const curr = labels[i];
                const prev = i > 0 ? labels[i - 1] : undefined;

                if (curr === '(') depth++;
                if (curr === ')') {
                    depth--;
                    if (depth < 0) return { status: false, msg: 'Parênteses fora de ordem.' };
                }

                // Ponto decimal isolado (chip "." sem dígitos).
                if (curr === '.') return { status: false, msg: 'Número decimal incompleto.' };

                // Falta operador antes de "(" : "30 (", "ind_plan_x (", ") (".
                if (curr === '(' && (this.isValueToken(prev) || prev === ')')) {
                    return { status: false, msg: 'Falta um operador antes do parêntese "(".' };
                }

                // Falta operador depois de ")" : ") 30", ") ind_plan_x".
                if (prev === ')' && this.isValueToken(curr)) {
                    return { status: false, msg: 'Falta um operador após o parêntese ")".' };
                }
            }

            return { status: true, msg: '' };
        },

        onSave() {
            // Equação vazia é permitida — representa a limpeza da Meta Plano do indicador.
            const labels = this.equationList.map(r => r.label);

            // (A.1) Validação estrutural compartilhada: parênteses (contagem),
            // operadores sequenciais, fim de operação, referências/funções repetidas.
            const shared = MediatorIndicatorEditor.validateEquation(labels);
            if (!shared.status) {
                MediatorIndicatorEditor.customToast(this.$bvToast, 'warning', 'Equação Inválida', shared.msg);
                return;
            }

            // (A.2) Checagens estruturais adicionais não cobertas pela validação compartilhada.
            const structural = this.validateStructure(labels);
            if (!structural.status) {
                MediatorIndicatorEditor.customToast(this.$bvToast, 'warning', 'Equação Inválida', structural.msg);
                return;
            }

            // (B) Rede de segurança: a equação precisa produzir um número. Pega casos não
            // mapeados (ex.: if/then/else incompleto, condição malformada). "Variável não
            // encontrada" é aceito — equação válida com dado do mês ausente.
            const equationStr = this.equationToString();
            const value = this.evaluateEquation(equationStr);
            if (labels.length > 0 && value === '-') {
                MediatorIndicatorEditor.customToast(this.$bvToast, 'warning', 'Equação Inválida',
                    'A equação não pôde ser calculada. Revise a estrutura (funções, condicionais ou parênteses).');
                return;
            }

            this.$emit('saved', equationStr, this.localComment, value);
        },

        onClose() {
            this.$emit('close');
        },
    },
};
</script>

<style scoped>
@import '../../indicatorEditor/commons/commonStyles.css';

.mp-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2050;
}

.mp-modal {
    background: #fff;
    border-radius: 6px;
    width: 96vw;
    max-width: 1400px;
    height: 78vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
    overflow: hidden;
}

/* Header */
.mp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #dee2e6;
    flex-shrink: 0;
    background: #fff;
}

.mp-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.mp-title i {
    color: #FF5F00;
    font-size: 1.1rem;
}

.mp-indicator-tag {
    font-weight: 400;
    font-size: 0.85rem;
    color: #777;
}

.mp-close-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #888;
    font-size: 1rem;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
}

.mp-close-btn:hover {
    background: #f0f0f0;
    color: #333;
}

/* Body */
.mp-body {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 1rem;
    padding: 0.75rem 1rem;
    flex: 1;
    overflow: hidden;
    min-height: 0;
}

/* ── Left column ─────────────────────────────── */
.mp-left {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    border-right: 1px solid #e8e8e8;
    padding-right: 0.75rem;
}

.harvest-col-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.88rem;
    font-weight: 600;
    color: #FF5F00;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #FF5F00;
    margin-bottom: 0.5rem;
    flex-shrink: 0;
}

/* Search */
.harvest-search-wrap {
    position: relative;
    flex-shrink: 0;
    margin-bottom: 0.4rem;
}

.harvest-search-icon {
    position: absolute;
    left: 0.55rem;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 0.8rem;
    pointer-events: none;
}

.harvest-search {
    width: 100%;
    padding: 0.4rem 0.4rem 0.4rem 1.8rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.82rem;
    color: #333;
    outline: none;
    transition: border-color 0.15s;
}

.harvest-search:focus {
    border-color: #FF5F00;
}

/* List */
.harvest-list {
    flex: 1;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-height: 0;
}

.harvest-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    font-size: 0.82rem;
    color: #888;
    gap: 0.4rem;
}

.harvest-item {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.35rem 0.55rem;
    font-size: 0.83rem;
    color: #1a1a1a;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    user-select: none;
}

.harvest-item:hover {
    background-color: #f5f5f5;
}

.harvest-item--active {
    background-color: rgba(0, 163, 159, 0.1);
}

.harvest-item-icon {
    color: #aaa;
    font-size: 0.75rem;
    flex-shrink: 0;
}

/* Hint */
.mp-dblclick-hint {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.72rem;
    color: #888;
    font-style: italic;
    padding: 0.3rem 0;
    flex-shrink: 0;
}

.mp-dblclick-hint i {
    color: #00A39F;
}

/* Selected item info (Image #6 style) */
.harvest-selected-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.4rem;
    flex-shrink: 0;
}

.harvest-info-field {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.harvest-info-label {
    font-size: 0.78rem;
    color: #555;
    font-weight: 500;
}

.harvest-info-text {
    font-size: 0.8rem;
    color: #333;
    padding: 0.3rem 0.45rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #fafafa;
    min-height: 1.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.harvest-value-input {
    width: 100%;
    padding: 0.3rem 0.45rem;
    border: 1px solid #00A39F;
    border-radius: 4px;
    font-size: 0.8rem;
    color: #00A39F;
    background: #fff;
    outline: none;
}

/* ── Right column ────────────────────────────── */
.mp-right {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    border: none !important;
}

/* Neutraliza estilos do commonStyles que não queremos */
.mp-right.real-equation,
.mp-right.prev-equation {
    border: none !important;
    height: auto !important;
}

/* Operations: grade 2 colunas com borda em cada grupo */
.operations {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    grid-template-rows: unset !important;
    gap: 0.4rem !important;
    padding: 0.4rem 0 !important;
    margin-top: 0 !important;
    border-bottom: 1px solid #e0e0e0 !important;
}

.op-container {
    border: 1px solid #d0d0d0;
    border-radius: 5px;
    padding: 0.4rem 0.55rem;
    background: #fafafa;
}

.op-container p {
    margin: 0 0 0.25rem !important;
    font-size: 0.78rem !important;
    font-weight: 600 !important;
    color: #333 !important;
}

/* Funções ocupa as 2 colunas */
.funcs {
    grid-column: 1 / -1;
}

/* Campo de Edição label */
.equation-label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #FF5F00;
    margin: 0.6rem 0 0.25rem;
}

/* Área de exibição da equação (chips) */
.equation-display {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 4.5rem;
    max-height: 9rem;
    background: #f1f1f1;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.5rem 0.6rem !important;
    display: flex !important;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 0.2rem;
    overflow-y: auto;
    box-sizing: border-box;
}

/* Chips dentro da equação: fundo teal opaco */
.equation-display ::v-deep .operation {
    background-color: rgba(0, 163, 159, 0.12);
    border-color: rgba(0, 163, 159, 0.5);
    color: #1a1a1a;
    width: auto;
    min-width: 2rem;
    padding: 0 0.6rem;
}

.equation-display ::v-deep .operation.deleteClass:hover {
    background-color: rgba(0, 163, 159, 0.08);
    border: 0.12rem dashed rgba(0, 163, 159, 0.7);
    color: #555;
}

/* Botões Limpar / Desfazer */
.eq-actions {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 0;
}

.eq-btn {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.9rem;
    border-radius: 4px;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    background: #fff;
}

.eq-btn--clear {
    border: 1px solid #dc3545;
    color: #dc3545;
}

.eq-btn--clear:hover {
    background: #fff5f5;
}

.eq-btn--undo {
    border: 1px solid #FF5F00;
    color: #FF5F00;
}

.eq-btn--undo:hover {
    background: #fff8f5;
}

/* Observation */
.mp-observation {
    margin-top: 0.5rem;
    padding: 0 0.5rem;
    flex-shrink: 0;
}

.mp-obs-label {
    margin: 0 0 0.3rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #2e2e2e;
    display: flex;
    align-items: center;
    gap: 0.3rem;
}

.mp-obs-label i {
    color: #FF5F00;
}

.mp-obs-textarea {
    width: 100%;
    border: 1px solid #FF5F00;
    border-radius: 4px;
    padding: 0.4rem 0.5rem;
    font-size: 0.85rem;
    resize: vertical;
    font-family: inherit;
    color: #333;
}

.mp-obs-textarea::placeholder {
    color: #aaa;
    font-style: italic;
}

.mp-obs-textarea:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 95, 0, 0.2);
}

/* Footer */
.mp-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0.6rem 1rem;
    border-top: 1px solid #dee2e6;
    background: #f8f9fa;
    flex-shrink: 0;
}

.mp-btn-cancel {
    padding: 0.45rem 1.1rem;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #555;
    cursor: pointer;
}

.mp-btn-cancel:hover {
    background: #f0f0f0;
}

.mp-btn-confirm {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 1.1rem;
    background: #FF5F00;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #fff;
    cursor: pointer;
}

.mp-btn-confirm:hover {
    background: #d94e00;
}
</style>
